import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useAuthStore } from "@/store/authStore";
import { homeApi } from "@/services/home";
import type { HomeBlock, HomeMetric, HomeQuickAction, HomeTaskItem } from "@/services/home";
import { HomeShell } from "./HomeShell";
// ═ COMPANY-STRIP BEGIN ═ COMPANY-CUT-1 · 订阅版首页（SAAS-1）
import { AssistantHome } from "./AssistantHome";
import { useEdition } from "@/theme/useTheme";
// ═ COMPANY-STRIP END ═
import { LoadingState } from "@/shared/ui/PageHeader";
import { RoomingListReminder } from "@/pages/orders/RoomingListReminder";
import { ProductHealthReminder } from "@/pages/products/ProductHealthReminder";

/**
 * 首页 · 角色个性化工作台 — 视觉严格对齐 sign-off canvas page-1（铁律十九 Canvas-as-Truth）。
 * 设计 token 取自 canvas：白卡 + 彩色数值 KPI / 严重度染色任务行 + 白胶囊按钮 / 彩色快捷 tile。
 * 定位「我今天该做什么」· 后端 GET /api/v1/home 按角色分发 · 真数据优先 · 缺引擎标占位。
 */
// ═ COMPANY-STRIP BEGIN ═
/** SAAS-1 · 版本类型选首页：订阅版（个人站）= 「经营助手」；企业版（默认 = 公司站）= 下面原来的角色首页，一个像素不变。 */
export function HomePage() {
  const edition = useEdition();
  return edition === "subscription" ? <AssistantHome /> : <EnterpriseHome />;
}
// ═ COMPANY-STRIP END ═
// ═ COMPANY-ONLY BEGIN ═
// export function HomePage() {
//   return <EnterpriseHome />;
// }
// ═ COMPANY-ONLY END ═

function EnterpriseHome() {
  const role = useAuthStore((s) => s.user?.role);
  const home = useQuery({
    queryKey: ["home", role],
    queryFn: () => homeApi.fetch(),
    enabled: !!role,
  });

  if (home.isLoading) return <LoadingState />;
  // A2/A3：API 失败时绝不能停在永久 loading（错误伪装成加载中）· 明确报错 + 重试
  if (home.isError || !home.data) return (
    <HomeShell>
      <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-red-200 bg-red-50/50 p-12">
        <p className="text-[13px] font-semibold text-red-700">首页数据加载失败</p>
        <p className="text-[11px] text-red-500">{(home.error as Error)?.message || "网络错误 · 请检查连接后重试"}</p>
        <button onClick={() => home.refetch()} disabled={home.isFetching} className="mt-1 rounded-md bg-blue-600 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-blue-700 disabled:opacity-40">{home.isFetching ? "重试中…" : "点击重试"}</button>
      </div>
    </HomeShell>
  );
  const d = home.data;

  return (
    <HomeShell subtitle={d.role_label.replace(/·.*/, "").trim()}>
      {/* HOTELENH ④ · Rooming List 待提交名单 OP todo（无待办时 render null·不改 canvas 基线）*/}
      <RoomingListReminder variant="home" />
      {/* PRODHEALTH · 产品可售性体检待办（无待办时 render null·给所有人看不做角色分层）*/}
      <ProductHealthReminder />

      {/* 角色横幅（canvas：标题 + 描述 + 红 tint「角色版本」药丸）*/}
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white px-5 py-4">
        <div>
          <div className="text-[17px] font-bold text-slate-900">{d.role_label}</div>
          <div className="mt-0.5 text-[12px] text-slate-500">{d.role_desc}</div>
        </div>
        <span
          className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-bold"
          style={{ backgroundColor: "#991B1B20", color: "#991B1B" }}
        >
          <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: "#991B1B" }} />
          角色版本
        </span>
      </div>

      {/* 4-6 KPI（canvas：白卡 + 灰边 + 彩色数值）*/}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {d.metrics.map((m) => (
          <Kpi key={m.id} m={m} />
        ))}
      </div>

      {/* 任务清单 + 快捷入口 blocks（canvas：2 列并排）*/}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {d.blocks.map((b, i) => (
          <BlockCard key={`${b.kind}-${i}`} block={b} />
        ))}
      </div>
    </HomeShell>
  );
}

// canvas 数值色：审批红 / 钱绿 / 蓝 accent / 紫 / 橙 · 占位走静音灰
const TONE_COLOR: Record<string, string> = {
  accent: "rgb(var(--c-blue-600))",   // THEME-1 · 主色随主题（classic = #2563EB，一个像素不变）
  danger: "#EF4444",
  warn: "#F59E0B",
  ok: "#10B981",
  lock: "#7C3AED",
};

function Kpi({ m }: { m: HomeMetric }) {
  const valueColor = m.placeholder ? "rgb(var(--c-slate-400))" : (m.tone && TONE_COLOR[m.tone]) || "rgb(var(--c-slate-900))";
  const clickable = !!m.href && !m.placeholder;
  const body = (
    <>
      <div className="flex items-center gap-1 text-[11px] font-semibold text-slate-500">
        {m.label}
        {m.placeholder && <span title="占位 · 引擎/模块待建" className="text-[10px] text-amber-500">⏳</span>}
        {clickable && <span className="ml-auto text-[11px] text-slate-300">→</span>}
      </div>
      <div className="mt-1 text-[22px] font-bold leading-tight" style={{ color: valueColor }}>
        {m.value}
      </div>
      {m.sub && <div className="mt-0.5 text-[10px] text-slate-400">{m.sub}</div>}
    </>
  );
  const base = "block rounded-xl border border-slate-200 bg-white p-4";
  return clickable ? (
    <Link to={m.href!} className={`${base} transition hover:border-blue-600/50 hover:shadow-sm`}>
      {body}
    </Link>
  ) : (
    <div className={base}>{body}</div>
  );
}

// 严重度染色（canvas：浅底 12 + 同色边 50）
const SEV_TINT: Record<string, { bg: string; border: string }> = {
  danger: { bg: "#EF444412", border: "#EF444450" },
  warn: { bg: "#F59E0B12", border: "#F59E0B50" },
  ok: { bg: "#10B98112", border: "#10B98150" },
  info: { bg: "rgb(var(--c-slate-50))", border: "rgb(var(--c-slate-200))" },
};

// 快捷 tile 强调色轮替（canvas：蓝/绿/紫/橙 彩色浅底）
// 第一块 = 主色（"brand" → 随主题）；其余固定彩色
const QUICK_ACCENTS = ["brand", "#10B981", "#7C3AED", "#F59E0B"];

function BlockCard({ block }: { block: HomeBlock }) {
  return (
    <section className="flex flex-col gap-3.5 rounded-xl border border-slate-200 bg-white p-5">
      <div className="flex items-start justify-between">
        <div className="text-[15px] font-bold text-slate-900">{block.title}</div>
        {block.placeholder && (
          <span className="shrink-0 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] text-amber-700">占位 · 待引擎</span>
        )}
      </div>

      {block.kind === "tasks" ? (
        <div className="flex flex-col gap-2.5">
          {block.tasks.map((t) => (
            <TaskRow key={t.id} t={t} />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
          {block.quick.map((q, i) => (
            <QuickTile key={`${q.label}-${i}`} q={q} accent={QUICK_ACCENTS[i % QUICK_ACCENTS.length]} />
          ))}
        </div>
      )}

      {block.note && <div className="text-[10px] text-slate-400">{block.note}</div>}
    </section>
  );
}

function TaskRow({ t }: { t: HomeTaskItem }) {
  const tint = SEV_TINT[t.severity] ?? SEV_TINT.info;
  return (
    <div
      className="flex items-center gap-3 rounded-lg border p-3"
      style={{ backgroundColor: tint.bg, borderColor: tint.border }}
    >
      <div className="min-w-0 flex-1">
        <div className="truncate text-[12px] font-semibold text-slate-900">
          {t.tag && <span className="mr-1 text-slate-500">{t.tag}</span>}
          {t.title}
        </div>
        <div className="truncate text-[10px] text-slate-500">{t.meta}</div>
      </div>
      {t.action && t.href && (
        <Link
          to={t.href}
          className="flex h-8 shrink-0 items-center rounded-full border border-slate-300 bg-white px-3 text-[12px] font-medium text-slate-700 hover:bg-slate-50"
        >
          {t.action}
        </Link>
      )}
    </div>
  );
}

function QuickTile({ q, accent }: { q: HomeQuickAction; accent: string }) {
  const inner = (
    <div
      className="flex h-full flex-col gap-1 rounded-[10px] border p-3.5 transition hover:brightness-95"
      style={accent === "brand" ? { backgroundColor: "rgb(var(--c-blue-600) / 0.08235)", borderColor: "rgb(var(--c-blue-600) / 0.25098)" } : { backgroundColor: `${accent}15`, borderColor: `${accent}40` }}
    >
      <div className="flex items-center gap-1.5 text-[13px] font-bold" style={{ color: accent === "brand" ? "rgb(var(--c-blue-600))" : accent }}>
        <span className="text-[15px] leading-none">{q.icon}</span>
        <span className="truncate">{q.label}</span>
      </div>
      {q.sub && <div className="truncate text-[10px] text-slate-500">{q.sub}</div>}
    </div>
  );
  return q.href ? (
    <Link to={q.href} className="block h-full">
      {inner}
    </Link>
  ) : (
    inner
  );
}
